import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('gasProvider');
export interface GasProviderInfoRec {
  id: string;
  userId: string;
  companyName?: string;
  accountNumber?: string;
  serviceAddress?: string;
  billingAddress?: string;
  customerServicePhone?: string;
  emergencyPhone?: string;
  website?: string;
  billingCycle?: string;
  monthlyCost?: number;
  autopayEnabled?: boolean;
  autopayMethod?: string;
  meterNumber?: string;
  shutoffLocation?: string;
  notes?: string;
  username?: string;
  password?: string;
  isLocked?: boolean;
}

class GasProviderInfoService {
  async getAllGasProviderInfo(targetUserId?: string): Promise<GasProviderInfoRec[]> {
    const url = targetUserId
      ? `/api/gas-provider-info?targetUserId=${targetUserId}`
      : '/api/gas-provider-info';
    const response = await http.get(url);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch gas provider information');
    }

    return response.data.data || [];
  }

  async getGasProviderInfoById(id: string): Promise<GasProviderInfoRec | null> {
    try {
      const response = await http.get(`/api/gas-provider-info/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch gas provider information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching gas provider information by ID:', error);
      return null;
    }
  }

  async createGasProviderInfo(gasProviderData: Partial<GasProviderInfoRec>): Promise<GasProviderInfoRec> {
    const response = await http.post('/api/gas-provider-info', gasProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save gas provider information');
    }

    return response.data.data;
  }

  async updateGasProviderInfo(id: string, gasProviderData: Partial<GasProviderInfoRec>): Promise<GasProviderInfoRec> {
    const response = await http.patch(`/api/gas-provider-info/${id}`, gasProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update gas provider information');
    }

    return response.data.data;
  }

  async deleteGasProviderInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/gas-provider-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete gas provider information');
    }
  }

  async lockGasProviderInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for gas provider ID:', id);
    const response = await http.post(`/api/gas-provider-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock gas provider information');
    }
  }

  async unlockGasProviderInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/gas-provider-info/${id}/unlock`, { granteeEmails });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock gas provider information');
    }
  }

  async getGasProviderAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/gas-provider-info/${id}/access-grants`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }
}

export const gasProviderInfoService = new GasProviderInfoService();
export default gasProviderInfoService;